"use client";
import facebookIcon from "@/assets/img/icons/facebook.svg";
import lnIcon from "@/assets/img/icons/linkedin.svg";
import twitterIcon from "@/assets/img/icons/twitter.svg";
import youtubeIcon from "@/assets/img/icons/youtube.svg";
import { useAppSelector } from "@/lib/hooks";
import { pageNumberSeo } from "@/models/seo";
import "@/styles/Footer/style.css";
import clsx from "clsx";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { memo, useMemo, useState } from "react";

interface FooterProps {}

const Footer = memo(function Footer({}: FooterProps) {
  const router = useRouter();
  const locale = useLocale();
  const { urls } = useAppSelector((state) => state.setting);
  const [aboutWidgetMobile, setAboutWidgetMobile] = useState<boolean>(false);
  const [contactWidgetMobile, setContactWidgetMobile] =
    useState<boolean>(false);
  const t = useTranslations("messages");
  const urlTerm = useMemo(() => {
    return urls?.find((item) => item.id === pageNumberSeo.TOS)?.slug;
  }, [urls]);
  return (
    <footer>
      <div className="footerWidget1">
        <div className="containerWidget1">
          <a
            href={`mailto:ask@cimigo.com`}
            className="emailContact no-underline font-semibold text-xl/[100%]"
            translation-key="footer_contact_us_at"
          >
            {t("footer_contact_us_at")}
          </a>
          <div className="linkSocial">
            <div className="socialContact">
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.facebook.com/CimigoVietnam"
                className="socialContactIcon"
              >
                <Image src={facebookIcon} alt="facebook logo" />
              </a>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.linkedin.com/company/cimigo/?originalSubdomain=vn"
                className="socialContactIcon"
              >
                <Image src={lnIcon} alt="linkedin logo" />
              </a>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://twitter.com/cimigovietnam?lang=en"
                className="socialContactIcon"
              >
                <Image src={twitterIcon} alt="twitter logo" />
              </a>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.youtube.com/channel/UC1lq4ngOGWl7NqGfsCOGbTA"
                className="socialContactIcon"
              >
                <Image src={youtubeIcon} alt="youtube logo" />
              </a>
            </div>
          </div>
        </div>
      </div>
      <div className="footerWidget2">
        <div className="containerWidget2">
          <div className="leftContainer">
            <div className="aboutWidget">
              <div className="header">
                <p
                  className="textDescription m-0"
                  translation-key="footer_about_cimigo"
                >
                  {t("footer_about_cimigo")}
                </p>
              </div>
              <div className="body">
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`https://www.cimigo.com/${locale}/`}
                  className="textLink"
                  translation-key="common_trends"
                >
                  {t("common_trends")}
                </a>
                <a
                  target="_blank"
                  translation-key="footer_opportunities"
                  rel="noopener noreferrer"
                  href={`https://www.cimigo.com/${locale}/opportunities-at-cimigo/`}
                  className="textLink"
                >
                  {t("footer_opportunities")}
                </a>
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`https://www.cimigo.com/${locale}/about-us/`}
                  className="textLink"
                  translation-key="footer_about_us"
                >
                  {t("footer_about_us")}
                </a>
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  className="textLink"
                  href={`${process.env.NEXT_PUBLIC_BASE_URL}/${locale}/terms-and-service`}
                  translation-key="footer_term_and_privacy"
                >
                  {t("footer_term_and_privacy")}
                </a>
              </div>
            </div>
            <div className="contactWidget">
              <div className="header">
                <p
                  className="textDescription m-0"
                  translation-key="footer_contact_us"
                >
                  {t("footer_contact_us")}
                </p>
              </div>
              <div className="body">
                <a
                  href={`tel:+(84)2838227727`}
                  className="textLink"
                  translation-key="common_phone"
                >
                  {t("common_phone")}: 028 3822 7727
                </a>
                {}
                <a className="textLink" translation-key="common_address">
                  {t("common_address")}: {t("common_address_value")}
                </a>
                <a
                  href={`mailto:ask@cimigo.com`}
                  className="textLink"
                  translation-key="common_email"
                >
                  {t("common_email")}: ask@cimigo.com
                </a>
              </div>
            </div>
            <div className="aboutWidgetMobile">
              <button
                className="headerMobile"
                onClick={() => setAboutWidgetMobile(!aboutWidgetMobile)}
              >
                <p
                  className="textDescriptionMobile m-0"
                  translation-key="footer_about_cimigo"
                >
                  {t("footer_about_cimigo")}
                </p>
                <div className="iconOpen"></div>
              </button>
              <div
                className={clsx("bodyMobile", {
                  ["bodyActive"]: aboutWidgetMobile,
                })}
              >
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://www.cimigo.com/en/"
                  className="textLink"
                  translation-key="common_trends"
                >
                  {t("common_trends")}
                </a>

                <a
                  target="_blank"
                  translation-key="footer_opportunities"
                  rel="noopener noreferrer"
                  href="https://www.cimigo.com/en/opportunities-at-cimigo/"
                  className="textLink"
                >
                  {t("footer_opportunities")}
                </a>

                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://www.cimigo.com/en/about-us/"
                  className="textLink"
                  translation-key="footer_about_us"
                >
                  {t("footer_about_us")}
                </a>
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  className="textLink"
                  href={`${process.env.NEXT_PUBLIC_BASE_URL}/${locale}/terms-and-service`}
                  translation-key="footer_market_research_reports"
                >
                  {t("footer_term_and_privacy")}
                </a>
              </div>
            </div>
            <div className="contactWidgetMobile">
              <button
                className="headerMobile"
                onClick={() => setContactWidgetMobile(!contactWidgetMobile)}
              >
                <p
                  className="textDescriptionMobile m-0"
                  translation-key="footer_contact_us"
                >
                  {t("footer_contact_us")}
                </p>
                <div className="iconOpen"></div>
              </button>
              <div
                className={clsx("bodyMobile", {
                  ["bodyActive"]: contactWidgetMobile,
                })}
              >
                <a
                  href={`tel:+(84)2838227727`}
                  className="textLink"
                  translation-key="common_phone"
                >
                  {t("common_phone")}: 028 3822 7727
                </a>
                {}
                <a className="textLink" translation-key="common_address">
                  {t("common_address")}: {t("common_address_value")}
                </a>
                <a
                  href={`mailto:ask@cimigo.com`}
                  className="textLink"
                  translation-key="common_email"
                >
                  {t("common_email")}: ask@cimigo.com
                </a>
              </div>
            </div>
          </div>
          <div className="rightContainer">
            <div className="mapController">
              <iframe
                className="iframcustom"
                title="map"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3919.143389361975!2d106.70470321533422!3d10.800327961704012!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x317528b2ccdd7be3%3A0x2e94e0c1b1e3a835!2sCimigo%20Market%20Research!5e0!3m2!1sen!2s!4v1618030557324!5m2!1sen!2s"
                allowFullScreen
                loading="lazy"
                frameBorder="0"
                width="500"
                height="235"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="footerWidget3">
        <div className="containerWidget3">
          <div className="textCopyRight">Copyright@2025.Cimigo</div>
        </div>
      </div>
    </footer>
  );
});

export default Footer;
